import { Controller } from "@hotwired/stimulus"
import { stream } from "@durable-streams/client"

export default class extends Controller {
  static targets = ["panel", "stepMessage", "stepCounter", "progressBar", "elapsedTime", "statusIcon"]
  static values = {
    autoReload: { type: Boolean, default: true },
    reloadDelay: { type: Number, default: 1500 }
  }

  connect() {
    this.abortController = null
    this.elapsedTimer = null
    this.startTime = null
    this._eventQueue = []
    this._processing = false
  }

  disconnect() {
    this.stopStreaming()
    this.stopElapsedTimer()
  }

  // Public: called by parent controllers to begin streaming
  // initialMessage: optional text to show immediately before stream events arrive
  startStreaming(streamUrl, { initialMessage } = {}) {
    if (!streamUrl) return

    if (initialMessage && this.hasStepMessageTarget) {
      this.stepMessageTarget.textContent = initialMessage
    }

    this.startTime = Date.now()
    this.showPanel()
    this.startElapsedTimer()
    this.connectToStream(streamUrl)
  }

  async connectToStream(streamUrl) {
    this.abortController = new AbortController()

    try {
      const res = await stream({
        url: streamUrl,
        offset: "-1",
        live: "sse",
        json: true,
        signal: this.abortController.signal
      })

      res.subscribeJson(async (batch) => {
        for (const item of batch.items) {
          this.handleEvent(item)
        }
      })

      res.closed.then(() => {
        this.abortController = null
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[streaming-progress] Stream error:", error)
      this.abortController = null
    }
  }

  handleEvent(event) {
    this._eventQueue.push(event)
    this._processQueue()
  }

  async _processQueue() {
    if (this._processing) return
    this._processing = true

    while (this._eventQueue.length > 0) {
      const event = this._eventQueue.shift()

      switch (event.type) {
        case "progress":
          this._applyProgress(event)
          // Ensure each step is visible for at least 600ms
          await new Promise(r => setTimeout(r, 600))
          break
        case "complete":
          this._applyComplete(event)
          this._processing = false
          return
        case "error":
          this._applyError(event)
          this._processing = false
          return
      }
    }

    this._processing = false
  }

  _applyProgress(event) {
    // Sync elapsed timer with server-side elapsed_ms (handles page refresh)
    if (event.elapsed_ms != null) {
      this.startTime = Date.now() - event.elapsed_ms
    }

    // Update step message with fade animation
    if (this.hasStepMessageTarget) {
      this.animateTextChange(this.stepMessageTarget, event.message)
    }

    // Update step counter
    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = `Step ${event.step} of ${event.total_steps}`
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${event.percent}%`
    }
  }

  _applyComplete(event) {
    this.stopElapsedTimer()
    this.stopStreaming()

    // Update UI to show completion
    if (this.hasStepMessageTarget) {
      this.animateTextChange(this.stepMessageTarget, event.message || "Complete")
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "100%"
      this.progressBarTarget.classList.remove("bg-brand-500")
      this.progressBarTarget.classList.add("bg-success-500")
    }

    // Show checkmark icon
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.innerHTML = `
        <svg class="w-5 h-5 text-success-600" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
        </svg>
      `
    }

    // Dispatch complete event for parent controllers
    this.dispatch("complete", { detail: event })

    // Auto-reload if configured
    if (this.autoReloadValue) {
      setTimeout(() => {
        window.location.reload()
      }, this.reloadDelayValue)
    }
  }

  _applyError(event) {
    this.stopElapsedTimer()
    this.stopStreaming()

    // Update UI to show error
    if (this.hasStepMessageTarget) {
      this.animateTextChange(this.stepMessageTarget, event.message || "An error occurred")
      this.stepMessageTarget.classList.add("text-error-600")
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove("bg-brand-500")
      this.progressBarTarget.classList.add("bg-error-500")
    }

    // Show error icon
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.innerHTML = `
        <svg class="w-5 h-5 text-error-600" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
        </svg>
      `
    }

    // Dispatch error event for parent controllers
    this.dispatch("error", { detail: event })
  }

  animateTextChange(target, newText) {
    target.style.opacity = "0"
    target.style.transform = "translateY(4px)"

    setTimeout(() => {
      target.textContent = newText
      target.style.opacity = "1"
      target.style.transform = "translateY(0)"
    }, 150)
  }

  showPanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("hidden")
    }
  }

  hidePanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("hidden")
    }
  }

  startElapsedTimer() {
    this.updateElapsedTime()
    this.elapsedTimer = setInterval(() => {
      this.updateElapsedTime()
    }, 1000)
  }

  stopElapsedTimer() {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer)
      this.elapsedTimer = null
    }
  }

  updateElapsedTime() {
    if (!this.hasElapsedTimeTarget || !this.startTime) return

    const elapsed = Math.floor((Date.now() - this.startTime) / 1000)
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60

    if (minutes > 0) {
      this.elapsedTimeTarget.textContent = `${minutes}m ${seconds}s`
    } else {
      this.elapsedTimeTarget.textContent = `${seconds}s`
    }
  }

  stopStreaming() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
